import {
  // createReference,
  ExtensionUrl,
  getExtensionValue,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type {
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireItemEnableWhen,
} from 'fhir/r4';

import {
  type Condition,
  type Questionnaire,
  QuestionnaireSchema,
  type QuestionnaireSection,
} from './schema';

const mapAnswerOptions = (
  questionnaireItemAnswerOption: QuestionnaireItemAnswerOption[],
) => {
  return questionnaireItemAnswerOption.map((answerOption) => {
    const priorityScoringValue = getExtensionValue(
      ExtensionUrl.riskAssessmentRiskScore,
    )(first(answerOption.extension));

    return {
      //TODO complex fhir types like valueCoding or valueReference are not supported
      value:
        answerOption.valueString ||
        answerOption.valueInteger ||
        answerOption.valueDate ||
        answerOption.valueTime ||
        // TODO: We need to use components for this so we have a better mapping to the UI.
        '',
      isDefaultAnswer: answerOption.initialSelected || false,
      priorityScoring: priorityScoringValue
        ? {
            value: priorityScoringValue,
          }
        : undefined,
    };
  });
};

const getCondition = (
  fhirCondition: QuestionnaireItemEnableWhen,
): Condition | undefined => {
  let value;
  if ('answerBoolean' in fhirCondition) {
    value = fhirCondition.answerBoolean;
  } else if ('answerString' in fhirCondition) {
    value = fhirCondition.answerString;
  } else if ('answerInteger' in fhirCondition) {
    value = fhirCondition.answerInteger;
  }
  if (value === undefined) return undefined;

  return {
    relatedLinkId: fhirCondition.question,
    operator: fhirCondition.operator,
    value: value,
  };
};
const mapSections = (section: QuestionnaireItem): QuestionnaireSection => {
  const {
    type,
    text,
    item = [],
    code,
    answerOption,
    linkId,
    required,
    enableWhen,
  } = section;
  const fhirCondition = first(enableWhen);
  return {
    text,
    icon: first(code)?.code,
    type,
    linkId,
    answerOptions: mapAnswerOptions(answerOption || []),
    required,
    condition: fhirCondition ? getCondition(fhirCondition) : undefined,
    questions: item.map((question) => {
      const { answerOption, linkId, type, text, enableWhen, required, item } =
        question;
      const fhirCondition = first(enableWhen);
      return {
        linkId,
        text,
        type,
        required,
        answerOptions: mapAnswerOptions(answerOption || []),
        condition: fhirCondition ? getCondition(fhirCondition) : undefined,
        questions: item?.map(mapSections),
      };
    }),
  };
};

export const mapFhirQuestionnaireToModel = (
  fhirQuestionnaire: fhir4.Questionnaire,
): Questionnaire => {
  const { item, title, code, url, id } = fhirQuestionnaire;

  const sections = item ? item.map(mapSections) : [];

  const questionnaire = {
    id,
    url,
    title,
    icon: first(code)?.code,
    section: sections,
  };

  const validatedQuestionnaire = QuestionnaireSchema.safeParse(questionnaire);
  if (!validatedQuestionnaire.success) {
    throw new Error(JSON.stringify(validatedQuestionnaire.error, null, 2));
  }

  return validatedQuestionnaire.data;
};
